// Angular Files
import {
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    Inject,
    ViewChild
} from '@angular/core';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    RouterEvent,
    Event,
    Router,
    RoutesRecognized
} from '@angular/router';
import { DOCUMENT } from '@angular/common';

// Angular Material Files
import { MatLegacyDialog, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MatSidenav } from '@angular/material/sidenav';

// Other External Files
import { Subscription, throwError } from 'rxjs';
import { filter } from 'rxjs/operators';

// Teller Online Files
import { AuthService } from './core/services';

// Teller Online Library Files
import { TellerOnlineAppService, TellerOnlineNotificationBannerService, TellerOnlineRailNavigationService } from 'teller-online-libraries/core';
import { TellerOnlineIconsRegistryService } from 'teller-online-libraries/icons';
import { SignInDialogComponent } from './shared/components';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    // ViewChild references
    @ViewChild('helpSidenav') helpSidenav: MatSidenav;

    @ViewChild('sideNav') set sideNavRef(sidenav: MatSidenav) {
        if (sidenav) {
            this._disableRailNavFocusTrap(sidenav);
        }
    }

    // Private Variables
    private _loginDialog: MatLegacyDialogRef<SignInDialogComponent>;

    // Subscriptions
    private _routerEventSubscription: Subscription;
    private _showLoginPromptSubscription: Subscription;

	constructor(
        @Inject(DOCUMENT) private document: Document,
        private cdr: ChangeDetectorRef,
		private router: Router,
        // we need to inject it so that it will be constructed, even though it's "not being used"
        private iconRegistry: TellerOnlineIconsRegistryService,
        public appService: TellerOnlineAppService,
        public authService: AuthService,
        public railNavigationService: TellerOnlineRailNavigationService,
        public notificationBannerService: TellerOnlineNotificationBannerService,
        public dialog: MatLegacyDialog
	) {
        // Initialize config-page-active class immediately to prevent flicker
        // (router events fire too late, so use location.pathname directly)
        if (this.document.location.pathname.startsWith('/config')) {
            this.document.body.classList.add('config-page-active');
        }

        this._routerEventSubscription = this._routerEventSubscription = this.router.events.pipe(
            filter((event: Event | RouterEvent) => event instanceof RouterEvent)
        ).subscribe((event: RouterEvent) => {
            this.handleRouterEventSubscription(event);
        });

        this._showLoginPromptSubscription = this.authService.showLoginPrompt$.subscribe(prompt => {
            if(prompt) {
                this._loginDialog = this.dialog.open(SignInDialogComponent, {
                    closeOnNavigation: false,
                    disableClose: true
                });
                this.appService.finishPageLoading();
                this.appService.finishLoading();
            } else {
                if(this._loginDialog) this._loginDialog.close();
            }
        });
	}

    ngOnInit() {
        this.appService.finishLoading();
    }

    /**
     * Angular Material bug workaround: when multiple sidenavs share a container and one has
     * mode="over", the container's hasBackdrop returns true, which incorrectly enables the
     * CDK focus trap on ALL sidenavs — including mode="side" ones. This monkey-patches
     * _updateFocusTrapState on the rail nav so the focus trap stays permanently disabled.
     */
    private _disableRailNavFocusTrap(sidenav: MatSidenav): void {
        const drawer = sidenav as any;
        const originalUpdate = drawer._updateFocusTrapState?.bind(drawer);
        if (originalUpdate) {
            drawer._updateFocusTrapState = () => {
                originalUpdate();
                if (drawer._focusTrap) {
                    drawer._focusTrap.enabled = false;
                }
            };
            if (drawer._focusTrap) {
                drawer._focusTrap.enabled = false;
            }
        }
    }

    public toggleHelp(): void {
        if (this.helpSidenav) {
            this.helpSidenav.toggle();
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardShortcut(event: KeyboardEvent) {
        // Ctrl+Alt+C → navigate to Config
        if (event.ctrlKey && event.altKey && event.key === 'c') {
            event.preventDefault();
            if (!this.router.url.startsWith('/config')) {
                this.router.navigate(['/config']);
            }
        }
    }

    ngOnDestroy() {
        if (this._routerEventSubscription) this._routerEventSubscription.unsubscribe();
        if (this._showLoginPromptSubscription) this._showLoginPromptSubscription.unsubscribe();
    }

    handleRouterEventSubscription(event: RouterEvent) {
        switch (true) {
            case event instanceof RoutesRecognized:
                // Set the routeTitle to be used later when setDocumentTitle is called

                //@ts-ignore
                let routeTitle = event.state?.root.firstChild.data?.title;

                // Profile has it's own routing module, so when on child pages of profile,
                // use the titles from that routing component instead.
                if (event.url.startsWith('/profile')) {
                    //@ts-ignore
                    routeTitle = event.state?.root.firstChild.firstChild.firstChild.data?.title;
                }

                if (event.url.startsWith('/config')) {
                    //@ts-ignore
                    routeTitle = event.state?.root.firstChild.firstChild.firstChild.data?.title;
                    this.document.body.classList.add('config-page-active');
                } else {
                    this.document.body.classList.remove('config-page-active');
                }

                this.appService.setRouteTitle(routeTitle);

                // Sync currentUrl and trigger change detection so Angular bindings (*ngIf) update
                // at the same time as the CSS class — prevents header showing wrong navigation
                // during the gap between RoutesRecognized and NavigationEnd.
                // No tellerLogon stripping needed here on the success path: it's a best-effort
                // strip (AuthService.captureTellerLogonFromUrl(), swallows a failed
                // history.replaceState) that runs before the Router's first navigation, and
                // nothing else in the app sets tellerLogon as a programmatic queryParams.
                this.appService.currentUrl = event.url;
                this.cdr.detectChanges();
            case event instanceof NavigationStart:
                // If we are attempting to logout via AD close the login prompt or else the page won't load properly
                let state = this.router.getCurrentNavigation()?.extras.state;
                if(state?.logout) {
                    this.authService.showLoginPrompt$.next(false);
                }
                // Close the help sidebar if navigating to the config help page or on mobile
                if (this.helpSidenav?.opened && (this.appService.isMobile || event.url.includes("config/help"))) {
                    this.toggleHelp();
                }
                let loadingMessage = "Loading...";
                this.appService.triggerLoading(loadingMessage);
                break;
            case event instanceof NavigationError:
                // @ts-ignore
                throwError(event.error);
            case event instanceof NavigationCancel:
                // Reset the authService password anytime we successfully navigate somewhere so it will be empty for any future requests
                this.appService.finishLoading();
                this.cdr.detectChanges();
                break;
            case event instanceof NavigationEnd:
                let loadedMessage = "Loaded";

                // Update the title attribute for the page
                this.appService.setDocumentTitle();

                this.appService.finishLoading(loadedMessage);

                //@ts-ignore
                // No tellerLogon stripping needed here either - see the same assignment above.
                this.appService.currentUrl = event.urlAfterRedirects;

                // remove the rail navigation if we're not on a rail page anymore
                if(!this.appService.railPage) this.railNavigationService.removeNavigation();

                // push out when we focus to ensure the html has actually rendered
                setTimeout(() => {
                    const mainFocus: HTMLElement = this.document.querySelector('#main-content-focus');

                    if (mainFocus) {
                        mainFocus.focus();
                    }
                }, 0);

                this.cdr.detectChanges();
                break;

            default: {
                break;
            }
        }
    }
}
